// ClientApp/src/routes/routeConfig.ts
import CellTowerIcon from '@mui/icons-material/CellTower'
import DashboardIcon from '@mui/icons-material/Dashboard'
import StoreIcon from '@mui/icons-material/Store'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { t, TFunction } from 'i18next'
import React from 'react'

// Components
import ProtectedReconciliationRoute from '../ProtectedReconciliationRoute'
import StepRedirector from '../StepRedirector'
import { Permissions, voidDelinquentPaymentPermissions } from '@/components/ProtectedComponent/permissionName'
import { isInProduction } from '@/config'
import { AuthInformation } from '@/models/api'
import paths from '@/utils/paths'

// Pages
const AccountAssociatedAccountsPage = React.lazy(() => import('../../pages/Account/AccountAssociatedAccounts'))
const AccountDelinquencyPage = React.lazy(() => import('../../pages/Account/AccountDelinquency'))
const AccountLayout = React.lazy(() => import('../../pages/Account/AccountLayout'))
const AccountNotesPage = React.lazy(() => import('../../pages/Account/AccountNotes/AccountNotesLoader'))
const AccountOverviewPage = React.lazy(() => import('../../pages/Account/AccountOverview'))
const AccountPaymentsPage = React.lazy(() => import('../../pages/Account/AccountPayments'))
const AccountPropertyAssetListPage = React.lazy(() => import('../../pages/Account/AccountPropertyAssetList'))
const AccountsPage = React.lazy(() => import('../../pages/Accounts'))
const AccountTypePage = React.lazy(() => import('../../pages/AccountTypes'))
const AccountUsersPage = React.lazy(() => import('../../pages/Account/AccountUsers'))
const AccountAssociatedLeaseAccountsPage = React.lazy(
  () => import('../../pages/Account/AccountAssociatedLeaseAccounts'),
)
const AdminUsersPage = React.lazy(() => import('../../pages/AdminUsers/AdminUsers'))
const BasicCostPage = React.lazy(() => import('../../pages/BasicCost'))
const CountySettingsPage = React.lazy(() => import('../../pages/CountySettings'))
const DelinquentAdjustmentPage = React.lazy(() => import('../../pages/Delinquency/DelinquentAdjustment'))
const DelinquentReturnedPaymentPage = React.lazy(() => import('../../pages/Payments/DelinquentReturnedPayment'))
const DelinquentSummaryPage = React.lazy(() => import('../../pages/Payments/DelinquentSummary'))
const ForgotPasswordPage = React.lazy(() => import('../../pages/ForgotPassword'))
const HomePage = React.lazy(() => import('../../pages/Home'))
const MassPaymentsPage = React.lazy(() => import('../../pages/Payments/MassPayments'))

// Lease Master
const LeaseMasterAccountNotesPage = React.lazy(
  () => import('../../pages/LeaseMaster/LeaseMasterOverviewSection/LeaseMasterAccountNotes'),
)
const LeaseMasterAccountsPage = React.lazy(() => import('../../pages/LeaseMaster/LessorAccounts'))
const LeaseMasterAreasPage = React.lazy(
  () => import('../../pages/LeaseMaster/LeaseMasterOverviewSection/LeaseMasterAreas'),
)
const LeaseMasterLayout = React.lazy(
  () => import('../../pages/LeaseMaster/LeaseMasterOverviewSection/LeaseMasterLayout'),
)
const LeaseMasterOverviewPage = React.lazy(
  () => import('../../pages/LeaseMaster/LeaseMasterOverviewSection/LeaseMasterOverview'),
)
const LeaseMasterPaymentPostingPage = React.lazy(() => import('../../pages/LeaseMaster/LeaseMasterPaymentPosting'))
const LeaseMasterPaymentReconciliationPage = React.lazy(
  () => import('../../pages/LeaseMaster/LeaseMasterPaymentReconciliation'),
)
const LeaseMasterAssetReconciliationPage = React.lazy(() => import('@/pages/LeaseMaster/AssetReconciliation'))
const MovePermitsPage = React.lazy(() => import('@/pages/MovePermits'))
const LeaseMasterDelinquency = React.lazy(
  () => import('@/pages/LeaseMaster/LeaseMasterOverviewSection/LeaseMasterDelinquency'),
)

// Telecom
const TelecomAccountsPage = React.lazy(() => import('../../pages/Telecom/TelecomAccounts'))

const TelecomLayout = React.lazy(() => import('../../pages/Telecom/TelecomOverviewSection/TelecomLayout'))

const TelecomOverviewPage = React.lazy(() => import('../../pages/Telecom/TelecomOverviewSection/TelecomOverview'))

const TelecomAreasPage = React.lazy(() => import('../../pages/Telecom/TelecomOverviewSection/TelecomAreas/Areas'))

const TelecomNotesPage = React.lazy(() => import('../../pages/Telecom/TelecomOverviewSection/TelecomNotes'))

const TelecomDelinquencyPage = React.lazy(
  () => import('../../pages/Telecom/TelecomOverviewSection/TelecomDelinquency'),
)

const TelecomPaymentPostingPage = React.lazy(() => import('../../pages/Telecom/TelecomPaymentPosting'))

const TelecomPaymentReconciliationPage = React.lazy(
  () => import('../../pages/Telecom/TelecomPaymentReconciliation'),
)

// Telecom Mcat
const McatLogin = React.lazy(() => import('../../pages/Mcat/McatLogin'))

const McatTelecomMaintenancePage = React.lazy(() => import('../../pages/Mcat/McatTelecomMaintenance'))

const McatHomePage = React.lazy(() => import('../../pages/Mcat/McatHome'))

const TelecomMcatAreasPage = React.lazy(
  () => import('../../pages/Telecom/TelecomOverviewSection/TelecomAreas/McatAreas'),
)

const LetterAndFormReportsPage = React.lazy(() => import('../../pages/ReportModule/reports/LetterAndForms'))
const Login = React.lazy(() => import('../../pages/Login'))
const MasterHomePage = React.lazy(() => import('../../pages/MasterHome'))
const MasterLogin = React.lazy(() => import('../../pages/MasterLogin'))
const NotificationFilesReportsPage = React.lazy(() => import('../../pages/ReportModule/reports/NotificationFiles'))
const PasswordReset = React.lazy(() => import('../../pages/PasswordReset'))
const PaymentSearchPage = React.lazy(() => import('../../pages/Payments/PaymentSearch'))
const PaymentSummaryPage = React.lazy(() => import('../../pages/Payments/PaymentSummary'))
const PaymentTotalsPage = React.lazy(() => import('../../pages/Payments/PaymentTotals'))
const ProfilePage = React.lazy(() => import('../../pages/Profile/Profile'))
const PropertyClassificationPage = React.lazy(() => import('../../pages/PropertyClassification'))
const ReconciliationAccountPage = React.lazy(() => import('../../pages/Reconciliation/Account'))
const ReconciliationContactsPage = React.lazy(() => import('../../pages/Reconciliation/Contacts'))
const ReconciliationLayout = React.lazy(() => import('../../pages/Reconciliation/Layout'))
const ReconciliationLessorsPage = React.lazy(() => import('../../pages/Reconciliation/Lessors'))
const ReconciliationPropertyAssetsPage = React.lazy(() => import('../../pages/Reconciliation/PropertyAssets'))
const ReconciliationSummaryPage = React.lazy(() => import('../../pages/Reconciliation/FilingSummary'))
const RefundsBatchPage = React.lazy(() => import('../../pages/Refunds/Batch/BatchPage'))
const RefundsNewBatchPage = React.lazy(() => import('../../pages/Refunds/Batch/NewBatch/NewBatchPage'))
const RefundsBatchSearchPage = React.lazy(() => import('../../pages/Refunds/Batch/Search/BatchSearchPage'))
const RefundsSearchPage = React.lazy(() => import('../../pages/Refunds/RefundsSearchPage'))
const ResetPasswordRequestedPage = React.lazy(() => import('../../pages/ForgotPassword/ResetPasswordRequested'))
const ReturnedPaymentPage = React.lazy(() => import('../../pages/Payments/ReturnedPayment'))
const RolesPage = React.lazy(() => import('../../pages/Roles/Roles'))
const SicCodeMastersPage = React.lazy(() => import('../../pages/SicCodeMasters'))
const SicCodePage = React.lazy(() => import('../../pages/SicCodes'))
const SicSiloCodePage = React.lazy(() => import('../../pages/SicCodes/SicSiloCodesLoader'))
const StatusReportsPage = React.lazy(() => import('../../pages/ReportModule/reports/StatusReports'))
const TaxAreaListReportPage = React.lazy(() => import('../../pages/ReportModule/reports/TaxAreaList'))
const SubSiloCodePage = React.lazy(() => import('../../pages/SicCodes/SubSiloCodesLoader'))
const VoidDelinquentPaymentPage = React.lazy(() => import('../../pages/Payments/VoidDelinquentPayment'))
const AppraisalRegionReportsPage = React.lazy(() => import('@/pages/ReportModule/reports/AppraisalRegionReports'))
const PaymentReportsPage = React.lazy(() => import('@/pages/ReportModule/reports/PaymentReports'))
const LeaseMasterReportsPage = React.lazy(() => import('@/pages/ReportModule/reports/LeaseMasterReports'))
const DelinquentPostcardReportPage = React.lazy(
  () => import('../../pages/ReportModule/reports/DelinquentPostcardReport'),
)
const EmailingReports = React.lazy(() => import('../../pages/ReportModule/reports/EmailingReports'))
const AccountReportsPage = React.lazy(() => import('@/pages/ReportModule/reports/AccountReports'))
const AccountListPrinterReport = React.lazy(
  () => import('@/pages/ReportModule/reports/AccountListForPrinterReport'),
)

const IronCountyTaxableValueReport = React.lazy(
  () => import('@/pages/ReportModule/reports/IronCountyTaxableValueReport'),
)

const EndOfYearReportPage = React.lazy(() => import('@/pages/ReportModule/reports/EndOfYearReports'))

const UnauthorizedPage = React.lazy(() => import('@/pages/UnauthorizedPage'))

const MassAccountCreationPage = React.lazy(() => import('@/pages/MassAccount'))

const BusinessBatchPenaltyPage = React.lazy(() => import('@/pages/BatchPenalty/BusinessBatchPenalty'))
const LeaseBatchPenaltyPage = React.lazy(() => import('@/pages/BatchPenalty/LeaseBatchPenalty'))
const TelecomBatchPenaltyPage = React.lazy(() => import('@/pages/BatchPenalty/TelecomBatchPenalty'))

// Lookup Table Maintenance
const LookupTableMaintenance = React.lazy(() => import('@/pages/TableMaintenance'))
const LookupTableAppraiserRegions = React.lazy(() => import('@/pages/TableMaintenance/AppraiserRegions'))
const LookupTableIndustrialParks = React.lazy(() => import('@/pages/TableMaintenance/IndustrialParks'))

// Email Module
const EmailConfiguration = React.lazy(() => import('@/pages/Email/EmailConfiguration/EmailConfiguration'))
const EmailBatch = React.lazy(() => import('@/pages/Email/EmailBatch/EmailBatch'))
const NewEmailBatch = React.lazy(() => import('@/pages/Email/EmailBatch/NewEmailBatch'))
const EmailBatchDetail = React.lazy(() => import('@/pages/Email/EmailBatchDetail/EmailBatchDetail'))

export type RouteType = {
  path?: string
  component: unknown
  children?: RouteType[]
  permission?: string | string[]

  // Quick Search properties
  title: string
  icon?: React.ReactNode
  description?: string
  section: string // e.g., "Lease Master", "Tax Reports", "Properties"
  subsection?: string // e.g., "Overview", "Settings"
  keywords?: string[] // additional keywords to improve searchability
  hideInQuickSearch?: boolean
}

export const publicRoutes: RouteType[] = [
  {
    path: paths.login,
    component: Login,
    title: t('login'),
    section: t('unauthenticated'),
  },
  {
    path: paths.forgotPassword,
    component: ForgotPasswordPage,
    title: t('forgotPassword'),
    section: t('unauthenticated'),
  },
  {
    path: paths.resetPasswordSent,
    component: ResetPasswordRequestedPage,
    title: t('forgotPassword'),
    section: t('unauthenticated'),
  },
  {
    path: paths.passwordReset,
    component: PasswordReset,
    section: t('unauthenticated'),
    title: t('chooseNewPassword'),
  },
  {
    path: paths.masterLogin,
    component: MasterLogin,
    title: t('signIn'),
    section: t('unauthenticated'),
  },
  {
    path: paths.mcatLogin,
    component: McatLogin,
    title: t('signIn'),
    section: t('unauthenticated'),
  },
  {
    path: paths.unauthorized,
    component: UnauthorizedPage,
    title: t('unauthorized'),
    section: t('unauthenticated'),
  },
]

export const getProtectedRoutes = (t: TFunction, jurisdiction?: number): RouteType[] => [
  // Home
  {
    path: paths.home,
    component: HomePage,
    title: t('dashboard'),
    icon: <DashboardIcon />,
    description: t('quickSearchDashboardDescription'),
    section: t('general'),
    subsection: t('general'),
    keywords: ['dashboard', 'main', 'quick links'],
  },

  // LeaseMaster section
  {
    path: paths.leaseMasterAccountsPage,
    component: LeaseMasterAccountsPage,
    title: t('leaseMasterAccounts'),
    section: t('leaseMaster'),
    subsection: t('leaseMaster'),
    description: t('quickSearchLeaseMasterAccountsDescription'),
    icon: <StoreIcon />,
    keywords: ['lease master', 'accounts', 'lessors', 'lessees', 'create lease master account'],
  },
  {
    component: LeaseMasterLayout,
    title: t('leaseMaster'),
    section: t('leaseMaster'),
    children: [
      {
        path: paths.leaseMasterOverviewPage,
        component: LeaseMasterOverviewPage,
        title: t('leaseMasterOverview'),
        subsection: t('leaseMaster'),
        description: t('quickSearchLeaseMasterOverviewDescription'),
        section: t('leaseMaster'),
        hideInQuickSearch: true,
      },
      {
        path: paths.leaseMasterAreasPage,
        component: LeaseMasterAreasPage,
        title: t('leaseMasterAreas'),
        description: t('quickSearchLeaseMasterAreasDescription'),
        section: t('leaseMaster'),
        subsection: t('area'),
        keywords: ['lease master', 'areas', 'manage areas'],
        hideInQuickSearch: true,
      },
      {
        path: paths.leaseMasterDelinquencyPage,
        component: LeaseMasterDelinquency,
        permission: Permissions.viewDelinquentPaymentEntry,
        title: t('leaseMasterDelinquency'),
        description: t('quickSearchLeaseMasterDelinquencyDescription'),
        section: t('leaseMaster'),
        subsection: t('delinquency'),
        keywords: ['lease master', 'delinquency', 'manage delinquency'],
        hideInQuickSearch: true,
      },
      {
        path: paths.leaseMasterNotesPage,
        component: LeaseMasterAccountNotesPage,
        title: t('leaseMasterNotes'),
        description: t('quickSearchLeaseMasterNotesDescription'),
        section: t('leaseMaster'),
        subsection: t('notes'),
        keywords: ['lease master', 'notes', 'account notes', 'comments'],
        hideInQuickSearch: true,
      },
    ],
  },
  {
    path: paths.leaseMasterPaymentPostingPage,
    component: LeaseMasterPaymentPostingPage,
    title: t('leaseMasterPostingPayments'),
    description: t('quickSearchLeaseMasterPostingPaymentsDescription'),
    section: t('leaseMaster'),
    subsection: t('leaseMaster'),
    keywords: ['lease master', 'payment posting', 'post payments'],
  },
  {
    path: paths.leaseMasterPaymentReconciliationPage,
    component: LeaseMasterPaymentReconciliationPage,
    title: t('leaseMasterPaymentReconciliation'),
    description: t('quickSearchLeaseMasterPaymentReconciliationDescription'),
    section: t('leaseMaster'),
    subsection: t('leaseMaster'),
    keywords: ['lease master', 'payment reconciliation', 'reconcile payments'],
  },
  {
    path: paths.leaseMasterAssetReconciliation,
    component: LeaseMasterAssetReconciliationPage,
    title: t('leasedAssetReconciliation'),
    description: t('quickSearchLeasedAssetReconciliationDescription'),
    section: t('leaseMaster'),
    subsection: t('assetReconciliation'),
    keywords: ['lease master', 'asset reconciliation', 'reconcile assets'],
  },

  // Telecom section
  {
    path: paths.telecomAccountsPage,
    component: TelecomAccountsPage,
    title: t('telecomAccounts'),
    description: t('quickSearchTelecomAccountsDescription'),
    section: t('telecom'),
    subsection: t('telecom'),
    icon: <CellTowerIcon />,
    keywords: ['telecom', 'accounts', 'create telecom account'],
  },
  {
    component: TelecomLayout,
    title: t('telecom'),
    section: t('telecom'),
    children: [
      {
        path: paths.telecomOverviewPage,
        component: TelecomOverviewPage,
        title: t('telecomOverview'),
        description: t('quickSearchTelecomOverviewDescription'),
        section: t('telecom'),
        keywords: ['telecom', 'overview', 'accouting summary', 'balance', 'telecom contacts'],
        hideInQuickSearch: true,
      },
      {
        path: paths.telecomAreasPage,
        component: TelecomAreasPage,
        title: t('telecomAreas'),
        description: t('quickSearchTelecomAreasDescription'),
        section: t('telecom'),
        subsection: t('area'),
        keywords: ['telecom', 'areas', 'manage areas'],
        hideInQuickSearch: true,
      },
      {
        path: paths.telecomNotesPage,
        component: TelecomNotesPage,
        title: t('telecomNotes'),
        description: t('quickSearchTelecomNotesDescription'),
        section: t('telecom'),
        subsection: t('notes'),
        keywords: ['telecom', 'notes', 'account notes', 'comments'],
        hideInQuickSearch: true,
      },
      {
        path: paths.telecomDelinquencyPage,
        component: TelecomDelinquencyPage,
        permission: Permissions.viewDelinquentPaymentEntry,
        title: t('telecomDelinquency'),
        description: t('quickSearchTelecomDelinquencyDescription'),
        section: t('telecom'),
        subsection: t('delinquency'),
        keywords: ['telecom', 'delinquency', 'manage delinquency', 'pay delinquent accounts'],
        hideInQuickSearch: true,
      },
    ],
  },
  {
    path: paths.telecomPaymentPostingPage,
    component: TelecomPaymentPostingPage,
    title: t('telecomPostingPayments'),
    description: t('quickSearchTelecomPostingPaymentsDescription'),
    section: t('telecom'),
    subsection: t('telecom'),
    keywords: ['telecom', 'payment posting', 'post payments'],
  },
  {
    path: paths.telecomPaymentReconciliationPage,
    component: TelecomPaymentReconciliationPage,
    title: t('telecomPaymentReconciliation'),
    description: t('quickSearchTelecomPaymentReconciliationDescription'),
    section: t('telecom'),
    subsection: t('telecom'),
    keywords: ['telecom', 'payment reconciliation', 'reconcile payments'],
  },

  // Account section
  {
    path: paths.accounts,
    component: AccountsPage,
    permission: Permissions.viewAccounts,
    title: t('accounts'),
    subsection: t('businessAccount'),
    icon: <StorefrontIcon />,
    description: t('quickSearchAccountsDescription'),
    section: t('accounts'),
    keywords: ['accounts', 'account list', 'create account'],
  },
  {
    component: AccountLayout,
    title: t('accounts'),
    section: t('accounts'),
    children: [
      {
        path: paths.accountOverview,
        component: AccountOverviewPage,
        permission: Permissions.viewAccounts,
        title: t('accountOverview'),
        description: t('quickSearchAccountOverviewDescription'),
        section: t('accounts'),
        subsection: t('overview'),
        keywords: ['account', 'overview', 'account details', 'summary information'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountAssociatedAccounts,
        component: AccountAssociatedAccountsPage,
        permission: Permissions.viewOwnersAssociatedAccounts,
        title: t('associatedAccounts'),
        description: t('quickSearchAssociatedAccountsDescription'),
        section: t('accounts'),
        subsection: t('associatedAccounts'),
        keywords: ['account', 'associated accounts'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountPropertyAssetList,
        component: AccountPropertyAssetListPage,
        permission: Permissions.viewAccountAssets,
        title: t('propertyAssetList'),
        description: t('quickSearchPropertyAssetListDescription'),
        section: t('accounts'),
        subsection: t('propertyAssetList'),
        keywords: ['account', 'property assets', 'manage property assets'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountPayments,
        component: AccountPaymentsPage,
        title: t('payments'),
        description: t('quickSearchPaymentsDescription'),
        section: t('accounts'),
        subsection: t('payments'),
        keywords: ['account', 'payments', 'manage payments'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountDelinquency,
        component: AccountDelinquencyPage,
        permission: Permissions.viewDelinquentPaymentEntry,
        title: t('accountDelinquency'),
        description: t('quickSearchAccountDelinquencyDescription'),
        section: t('accounts'),
        subsection: t('delinquency'),
        keywords: ['account', 'delinquency', 'manage delinquency'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountNotes,
        component: AccountNotesPage,
        title: t('notes'),
        description: t('quickSearchNotesDescription'),
        section: t('accounts'),
        subsection: t('notes'),
        keywords: ['account', 'notes', 'manage notes', 'comments'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountUsers,
        component: AccountUsersPage,
        title: t('users'),
        description: t('quickSearchUsersDescription'),
        section: t('accounts'),
        subsection: t('users'),
        keywords: ['account', 'users', 'manage users', 'account users'],
        hideInQuickSearch: true,
      },
      {
        path: paths.accountAssociatedLeaseAccounts,
        component: AccountAssociatedLeaseAccountsPage,
        title: t('associatedLeaseAccounts'),
        section: t('accounts'),
        subsection: t('associatedLeaseAccounts'),
        hideInQuickSearch: true,
      },
    ],
  },

  // Reconciliation section
  {
    component: ProtectedReconciliationRoute,
    section: t('reconciliation'),
    title: t('reconciliation'),
    children: [
      {
        component: ReconciliationLayout,
        section: t('reconciliation'),
        title: t('reconciliation'),
        children: [
          {
            path: paths.reconciliation,
            component: StepRedirector,
            title: t('reconciliation'),
            section: t('reconciliation'),
            hideInQuickSearch: true,
          },
          {
            path: paths.reconciliationAccountInformation,
            component: ReconciliationAccountPage,
            title: t('basicAccountInformation'),
            section: t('reconciliation'),
            subsection: t('basicAccountInformation'),
            hideInQuickSearch: true,
          },
          {
            path: paths.reconciliationContacts,
            component: ReconciliationContactsPage,
            title: t('contacts'),
            section: t('reconciliation'),
            subsection: t('contacts'),
            hideInQuickSearch: true,
          },
          {
            path: paths.reconciliationPropertyAssets,
            component: ReconciliationPropertyAssetsPage,
            title: t('propertyAssetList'),
            section: t('reconciliation'),
            hideInQuickSearch: true,
          },
          {
            path: paths.reconciliationLeasedAssets,
            component: ReconciliationLessorsPage,
            title: t('leasedAssets'),
            section: t('reconciliation'),
            hideInQuickSearch: true,
          },
          {
            path: paths.reconciliationSummary,
            component: ReconciliationSummaryPage,
            title: t('summary'),
            section: t('reconciliation'),
            hideInQuickSearch: true,
          },
        ],
      },
    ],
  },

  // Reports section
  {
    path: paths.accountReports,
    component: AccountReportsPage,
    title: t('accountReports'),
    description: t('quickSearchAccountReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'account reports', 'generate reports'],
  },
  {
    path: paths.letterAndFormReports,
    component: LetterAndFormReportsPage,
    title: t('letterAndForms'),
    description: t('quickSearchLetterAndFormsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'letters', 'forms', 'generate letters', 'generate forms'],
  },
  {
    path: paths.notificationFileReports,
    component: NotificationFilesReportsPage,
    title: t('notificationFiles'),
    description: t('quickSearchNotificationFilesDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'notification files', 'generate notification files'],
  },
  {
    path: paths.statusReport,
    component: StatusReportsPage,
    title: t('statusReports'),
    description: t('quickSearchStatusReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'status reports', 'view status reports'],
  },
  {
    path: paths.appraisalRegionReports,
    component: AppraisalRegionReportsPage,
    title: t('appraisalRegionReports'),
    description: t('appraisalRegionReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'appraisal region'],
  },
  {
    path: paths.paymentReports,
    component: PaymentReportsPage,
    title: t('paymentReports'),
    description: t('paymentReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'payments'],
  },
  {
    path: paths.leaseMasterReports,
    component: LeaseMasterReportsPage,
    title: t('leaseMasterReports'),
    description: t('leaseMasterReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'lease master'],
  },
  {
    path: paths.taxAreaList,
    component: TaxAreaListReportPage,
    title: t('taxAreaListReport'),
    description: t('quickSearchTaxAreaListReportDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'tax area list', 'view tax areas'],
    permission: Permissions.reportTaxAreas,
  },
  {
    path: paths.delinquentPostcardReport,
    component: DelinquentPostcardReportPage,
    permission: Permissions.reportDelinquentPostcard,
    title: t('delinquentPostcardReport'),
    description: t('quickSearchDelinquentPostcardReportDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'delinquent postcard', 'view postcards'],
  },
  {
    path: paths.emailingReports,
    component: EmailingReports,
    permission: Permissions.reportEarlyOnlineEmailList,
    title: t('emailingReports'),
    description: t('quickSearchEmailingReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'emailing', 'manage emailing reports'],
  },
  {
    path: paths.accountListForPrinterReport,
    component: AccountListPrinterReport,
    permission: Permissions.reportAccountListForPrinter,
    title: t('accountListForPrinterReport'),
    description: t('quickSearchAccountListForPrinterReportDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'account list', 'printer report', 'view account lists'],
  },
  {
    path: paths.endOfYearReports,
    component: EndOfYearReportPage,
    title: t('yearEndReports'),
    description: t('quickSearchYearEndReportsDescription'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    keywords: ['reports', 'end of year', 'view year-end reports'],
    permission: Permissions.eoyCheckListReport,
  },
  {
    path: paths.movePermits,
    component: MovePermitsPage,
    permission: Permissions.searchMovePermits,
    title: t('movePermit'),
    section: t('reportModule'),
    subsection: t('reportModule'),
    description: t('quickSearchMovePermitDescription'),
    keywords: ['report', 'move permit', 'permit report'],
    hideInQuickSearch: isInProduction,
  },

  // Payments section
  {
    path: paths.delinquentAdjustment,
    component: DelinquentAdjustmentPage,
    permission: Permissions.viewDelinquentAdjustment,
    title: t('delinquentAdjustment'),
    section: t('payments'),
  },
  {
    path: paths.paymentSearch,
    component: PaymentSearchPage,
    title: t('paymentSearch'),
    section: t('payments'),
    subsection: t('payments'),
    permission: Permissions.searchPayments,
  },
  {
    path: paths.paymentSummary,
    component: PaymentSummaryPage,
    title: t('paymentSummary'),
    section: t('payments'),
    subsection: t('payments'),
    permission: Permissions.viewCurrentYearSummary,
  },
  {
    path: paths.paymentTotals,
    component: PaymentTotalsPage,
    title: t('paymentTotals'),
    section: t('payments'),
    subsection: t('payments'),
    permission: Permissions.viewDailyPaymentTotals,
  },
  {
    path: paths.delinquentSummary,
    component: DelinquentSummaryPage,
    title: t('delinquentSummary'),
    section: t('payments'),
    subsection: t('payments'),
    permission: Permissions.viewDelinquentPaymentSummary,
  },
  {
    path: paths.paymentReturnedPayment,
    component: ReturnedPaymentPage,
    title: t('currentYearReturnedPayment'),
    section: t('payments'),
    subsection: t('payments'),
    permission: [Permissions.viewCurrentYearReturnedCheckInformation, Permissions.createCurrentYearReturnedCheck],
  },
  {
    path: paths.paymentDelinquentYear,
    component: DelinquentReturnedPaymentPage,
    title: t('delinquentYearReturnedPayment'),
    section: t('payments'),
    subsection: t('payments'),
    permission: [Permissions.viewDelinquentYearReturnedCheck, Permissions.saveDelinquentYearReturnedCheck],
  },
  {
    path: paths.voidDelinquentPayment,
    component: VoidDelinquentPaymentPage,
    permission: voidDelinquentPaymentPermissions,
    title: t('voidDelinquentPayment'),
    section: t('payments'),
    subsection: t('payments'),
  },
  {
    path: paths.massPayments,
    component: MassPaymentsPage,
    title: t('massPayments'),
    section: t('payments'),
    subsection: t('payments'),
  },

  // Refunds section
  {
    path: paths.refundsBatch,
    component: RefundsNewBatchPage,
    title: t('refundCreateBatch'),
    section: t('refunds'),
    hideInQuickSearch: true,
  },
  {
    path: paths.refundsBatchWithId,
    component: RefundsBatchPage,
    title: t('refundBatch'),
    section: t('refunds'),
    hideInQuickSearch: true,
  },
  {
    path: paths.refundsBatchSearch,
    component: RefundsBatchSearchPage,
    title: t('refundBatches'),
    section: t('refunds'),
    description: t('quickSearchRefundBatchesDescription'),
    permission: Permissions.viewRefundBatch,
  },
  {
    path: paths.refundsSearch,
    component: RefundsSearchPage,
    title: t('searchPendingRefunds'),
    section: t('refunds'),
    description: t('quickSearchSearchPendingRefundsDescription'),
    permission: Permissions.searchRefunds,
  },

  // Miscellaneous protected routes
  {
    path: paths.accountType,
    component: AccountTypePage,
    section: t('adminMaintenance'),
    title: t('accountType'),
    subsection: t('adminMaintenance'),
    description: t('quickSearchAccountTypeDescription'),
    permission: Permissions.accountType,
  },
  {
    path: paths.adminUsers,
    component: AdminUsersPage,
    title: t('adminUsers'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
    permission: Permissions.getAllAdminUsers,
    description: t('quickSearchAdminUsersDescription'),
  },
  {
    path: paths.basicCost,
    component: BasicCostPage,
    title: t('basicCost'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
    description: t('quickSearchBasicCostDescription'),
  },
  {
    path: paths.countySettings,
    component: CountySettingsPage,
    title: t('countySettings'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
    description: t('quickSearchCountySettingsDescription'),
    permission: Permissions.countySettings,
  },
  {
    path: paths.propertyClassification,
    component: PropertyClassificationPage,
    title: t('propertyClassification'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
    description: t('quickSearchPropertyClassificationDescription'),
    permission: Permissions.propertyClassification,
  },
  {
    path: paths.profile,
    component: ProfilePage,
    title: t('profile'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
    description: t('quickSearchProfileDescription'),
  },
  {
    path: paths.roles,
    component: RolesPage,
    title: t('roles'),
    section: t('adminMaintenance'),
    permission: Permissions.getRole,
    description: t('quickSearchRolesDescription'),
  },
  {
    path: paths.sicCodeMasters,
    component: SicCodeMastersPage,
    title: t('sicCodeMaster'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
  },
  {
    path: paths.sicCodes,
    component: SicCodePage,
    title: t('sicCodes'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
  },
  {
    path: paths.sicSiloCodes,
    component: SicSiloCodePage,
    title: t('sicSiloCode'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
  },
  {
    path: paths.subSiloCodes,
    component: SubSiloCodePage,
    title: t('subSiloCode'),
    section: t('adminMaintenance'),
    subsection: t('adminMaintenance'),
  },

  // Batch
  {
    path: paths.massAccounts,
    component: MassAccountCreationPage,
    title: t('massAccount'),
    section: t('batch'),
    subsection: t('batch'),
    hideInQuickSearch: jurisdiction === 11,
  },
  {
    path: paths.businessBatchPenalty,
    component: BusinessBatchPenaltyPage,
    title: t('businessBatchPenalty'),
    section: t('batch'),
    subsection: t('batch'),
  },
  {
    path: paths.leaseBatchPenalty,
    component: LeaseBatchPenaltyPage,
    title: t('leaseBatchPenalty'),
    section: t('batch'),
    subsection: t('batch'),
  },

  // Tyler Integration
  {
    path: paths.ironCountyTaxableValueReport,
    component: IronCountyTaxableValueReport,
    title: t('ironCountyTaxableValueReport'),
    section: t('tylerIntegration'),
    subsection: t('tylerIntegration'),
    keywords: ['reports', 'iron county', 'taxable value', 'tyler', 'integration'],
    hideInQuickSearch: jurisdiction !== 11,
  },
  {
    path: paths.tylerIntegrationMassAccounts,
    component: MassAccountCreationPage,
    title: t('massAccount'),
    section: t('tylerIntegration'),
    subsection: t('tylerIntegration'),
    keywords: ['tyler', 'integration', 'mass account creation'],
    hideInQuickSearch: jurisdiction !== 11,
  },

  // Lookup Table Maintenance
  {
    path: paths.lookupTableMaintenance,
    component: LookupTableMaintenance,
    title: t('lookupTableMaintenance'),
    section: t('lookupTableMaintenance'),
    subsection: t('lookupTableMaintenance'),
    keywords: ['table', 'maintenance', 'lookup'],
  },
  {
    path: paths.lookupTableAppraiserRegions,
    component: LookupTableAppraiserRegions,
    title: t('appraiserRegions'),
    section: t('lookupTableMaintenance'),
    subsection: t('lookupTableMaintenance'),
    keywords: ['appraiser', 'regions', 'lookup'],
  },
  {
    path: paths.lookupTableIndustrialParks,
    component: LookupTableIndustrialParks,
    title: t('industrialParks'),
    section: t('lookupTableMaintenance'),
    subsection: t('lookupTableMaintenance'),
    keywords: ['industrial', 'park', 'lookup'],
  },

  // Email Module
  {
    path: paths.emailBatch,
    component: EmailBatch,
    title: 'Email Batches',
    section: 'Emails',
    subsection: 'Emails',
    keywords: ['email', 'batch', 'email batch'],
  },
  {
    path: paths.emailBatchNew,
    component: NewEmailBatch,
    title: 'New Email Batch',
    section: 'Emails',
    subsection: 'Emails',
    keywords: ['email', 'batch', 'new email batch'],
  },
  {
    path: paths.emailBatchDetail,
    component: EmailBatchDetail,
    title: 'Email Batch Detail',
    section: 'Emails',
    subsection: 'Emails',
    keywords: ['email', 'batch', 'email batch detail'],
  },
  {
    path: paths.emailConfiguration,
    component: EmailConfiguration,
    title: 'Email Configuration',
    section: 'Emails',
    subsection: 'Emails',
    keywords: ['email', 'configuration', 'email configuration'],
  },
]

export const getProtectedMasterRoutes = (t: TFunction): RouteType[] => [
  {
    path: paths.masterHome,
    component: MasterHomePage,
    title: t('dashboard'),
    section: t('general'),
    subsection: t('dashboard'),
  },
  {
    path: paths.masterAccountType,
    component: AccountTypePage,
    title: t('accountType'),
    section: t('adminMaintenance'),
    subsection: t('accountType'),
  },
  {
    path: paths.masterBasicCost,
    component: BasicCostPage,
    title: t('basicCost'),
    section: t('adminMaintenance'),
    subsection: t('basicCost'),
  },
  {
    path: paths.masterPropertyClassification,
    component: PropertyClassificationPage,
    title: t('propertyClassification'),
    section: t('adminMaintenance'),
    subsection: t('propertyClassification'),
  },
]

export const getProtectedMcatRoutes = (t: TFunction): RouteType[] => [
  {
    path: paths.mcatHome,
    component: McatHomePage,
    title: t('dashboard'),
    section: t('general'),
    icon: <DashboardIcon />,
    description: t('quickSearchDashboardDescription'),
    subsection: t('general'),
    keywords: ['dashboard', 'main', 'overview', 'quick links', 'mcat'],
  },
  {
    path: paths.mcatTelecomAccountsPage,
    component: TelecomAccountsPage,
    title: t('telecom'),
    icon: <CellTowerIcon />,
    description: t('quickSearchTelecomAccountsDescription'),
    section: t('telecom'),
    subsection: t('accounts'),
    keywords: ['telecom', 'accounts', 'create telecom account'],
  },
  {
    path: paths.mcatTelecomMaintenancePage,
    component: McatTelecomMaintenancePage,
    title: t('telecomMaintenancePage'),
    icon: <CellTowerIcon />,
    description: t('quickSearchTelecomMaintenanceDescription'),
    section: t('telecom'),
    subsection: t('accounts'),
    keywords: ['telecom', 'deactivate telecom account'],
  },
  {
    component: TelecomLayout,
    title: t('telecom'),
    section: t('telecom'),
    children: [
      {
        path: paths.mcatTelecomOverviewPage,
        component: TelecomOverviewPage,
        title: t('telecomOverview'),
        description: t('quickSearchTelecomOverviewDescription'),
        section: t('telecom'),
        subsection: t('overview'),
        keywords: ['telecom', 'overview', 'account summary', 'filing status'],
        hideInQuickSearch: true,
      },
      {
        path: paths.mcatTelecomAreasPage,
        component: TelecomMcatAreasPage,
        title: t('telecomAreas'),
        description: t('quickSearchTelecomAreasDescription'),
        section: t('telecom'),
        subsection: t('area'),
        keywords: ['telecom', 'areas', 'manage areas', 'mcat'],
        hideInQuickSearch: true,
      },
      {
        path: paths.mcatTelecomNotesPage,
        component: TelecomNotesPage,
        title: t('telecomNotes'),
        description: t('quickSearchTelecomNotesDescription'),
        section: t('telecom'),
        subsection: t('notes'),
        keywords: ['telecom', 'notes', 'manage notes', 'mcat'],
        hideInQuickSearch: true,
      },
    ],
  },
  {
    path: paths.telecomBatchPenalty,
    component: TelecomBatchPenaltyPage,
    title: t('telecomBatchPenalty'),
    section: t('batch'),
    subsection: t('batch'),
  },
]

export function generateSearchableRoutes(
  routes: RouteType[],
  hasPermission?: (permission?: string | string[]) => boolean,
): RouteType[] {
  return routes.reduce<RouteType[]>((acc, route) => {
    const { permission, children, hideInQuickSearch } = route

    const hasRequiredPermission = !permission || !hasPermission || hasPermission(permission)

    if (!hasRequiredPermission || hideInQuickSearch) {
      return acc
    }

    if (children && children.length > 0) {
      return [...acc, ...generateSearchableRoutes(children, hasPermission)]
    }

    return [...acc, route]
  }, [])
}

interface GetSearchableRoutesParams {
  hasPermission: (permission?: string | string[]) => boolean
  authInformation: AuthInformation
  t: TFunction
}

export function getSearchableRoutes({
  hasPermission,
  authInformation,
  t,
}: GetSearchableRoutesParams): RouteType[] {
  let routes: RouteType[] = []

  if (!authInformation) {
    return []
  }

  if (authInformation.isMasterUser) {
    routes = getProtectedMasterRoutes(t)
  } else if (authInformation.isMcatUser) {
    routes = getProtectedMcatRoutes(t)
  } else {
    routes = getProtectedRoutes(t, authInformation.jurisdictionId as number)
  }

  return generateSearchableRoutes(routes, hasPermission)
}
